import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowRight, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type RedirectItem = {
  id: string | null;
  source_path: string;
  destination_path: string;
  status_code: number;
  active: boolean;
  note: string;
};

const emptyRedirect: RedirectItem = {
  id: null,
  source_path: "",
  destination_path: "",
  status_code: 301,
  active: true,
  note: "",
};

const fieldClass = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";
const labelClass = "block text-xs font-medium text-foreground";

export const Route = createFileRoute("/admin/redirecionamentos")({
  ssr: false,
  head: () => ({ meta: [{ title: "Redirecionamentos | Painel" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: RedirectsAdmin,
});

function normalizePath(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const withoutOrigin = trimmed.replace(/^https?:\/\/[^/]+/i, "");
  const path = withoutOrigin.startsWith("/") ? withoutOrigin : `/${withoutOrigin}`;
  if (path === "/" || path.includes(".") || path.startsWith("/api/") || path.startsWith("/admin/")) return path;
  return path.endsWith("/") ? path : `${path}/`;
}

function RedirectsAdmin() {
  const [items, setItems] = useState<RedirectItem[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    const { data, error } = await supabase
      .from("redirects")
      .select("id, source_path, destination_path, status_code, active, note")
      .order("source_path");
    if (error) {
      setMessage(`Não foi possível carregar: ${error.message}`);
      return;
    }
    setItems((data ?? []).map((item) => ({ ...item, note: item.note ?? "" })));
  }

  useEffect(() => {
    void load();
  }, []);

  function update(index: number, key: keyof RedirectItem, value: string | number | boolean) {
    setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  }

  async function save(item: RedirectItem) {
    const source = normalizePath(item.source_path);
    const destination = normalizePath(item.destination_path);
    setMessage(null);
    if (!source || !destination) return setMessage("Preencha o endereço antigo e o novo.");
    if (source === destination) return setMessage("O endereço antigo e o novo não podem ser iguais.");
    if (source.startsWith("/admin/") || source.startsWith("/api/")) return setMessage("Endereços internos do painel e do sistema não podem ser redirecionados.");

    const record = {
      source_path: source,
      destination_path: destination,
      status_code: item.status_code,
      active: item.active,
      note: item.note.trim() || null,
      updated_at: new Date().toISOString(),
    };
    const result = item.id
      ? await supabase.from("redirects").update(record).eq("id", item.id)
      : await supabase.from("redirects").insert(record);
    if (result.error) return setMessage(`Não foi possível salvar: ${result.error.message}`);
    setMessage(`Redirecionamento de ${source} salvo.`);
    void load();
  }

  async function remove(item: RedirectItem) {
    if (!item.id) {
      setItems((current) => current.filter((candidate) => candidate !== item));
      return;
    }
    if (!window.confirm(`Excluir o redirecionamento de ${item.source_path}?`)) return;
    const { error } = await supabase.from("redirects").delete().eq("id", item.id);
    if (error) return setMessage(`Não foi possível excluir: ${error.message}`);
    setMessage("Redirecionamento excluído.");
    void load();
  }

  return (
    <section>
      <h1 className="text-lg font-semibold text-foreground">Redirecionamentos</h1>
      <p className="mt-2 text-sm text-muted-foreground">Cadastre aqui os endereços antigos do WordPress e para onde cada um deve levar no site novo.</p>
      {message ? <p className="mt-4 rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">{message}</p> : null}
      <div className="mt-6 space-y-4">
        {items.map((item, index) => (
          <div key={item.id ?? `new-${index}`} className="grid gap-3 rounded-md border border-border p-4 md:grid-cols-[1fr_auto_1fr]">
            <div>
              <label className={labelClass} htmlFor={`source-${index}`}>Endereço antigo</label>
              <input id={`source-${index}`} className={fieldClass} placeholder="/pagina-antiga/" value={item.source_path} onChange={(event) => update(index, "source_path", event.target.value)} />
            </div>
            <ArrowRight className="mt-6 size-5 text-muted-foreground" aria-hidden="true" />
            <div>
              <label className={labelClass} htmlFor={`destination-${index}`}>Novo endereço</label>
              <input id={`destination-${index}`} className={fieldClass} placeholder="/pagina-nova/" value={item.destination_path} onChange={(event) => update(index, "destination_path", event.target.value)} />
            </div>
            <div>
              <label className={labelClass} htmlFor={`status-${index}`}>Tipo</label>
              <select id={`status-${index}`} className={fieldClass} value={item.status_code} onChange={(event) => update(index, "status_code", Number(event.target.value))}>
                <option value={301}>301, permanente</option>
                <option value={302}>302, temporário</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex min-h-10 items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={item.active} onChange={(event) => update(index, "active", event.target.checked)} />
                Ativo
              </label>
            </div>
            <div>
              <label className={labelClass} htmlFor={`note-${index}`}>Observação</label>
              <input id={`note-${index}`} className={fieldClass} value={item.note} onChange={(event) => update(index, "note", event.target.value)} />
            </div>
            <div className="flex gap-2 md:col-span-3">
              <Button type="button" onClick={() => void save(item)}>Salvar</Button>
              <Button type="button" variant="ghost" onClick={() => void remove(item)}><Trash2 className="mr-2 size-4" aria-hidden="true" />Excluir</Button>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" className="mt-4" onClick={() => setItems((current) => [...current, { ...emptyRedirect }])}>
        <Plus className="mr-2 size-4" aria-hidden="true" />Novo redirecionamento
      </Button>
    </section>
  );
}